import { Box, Stack } from '@mui/material'
import { ArrowUpRightIcon, InfoIcon, PlusCircleIcon } from '@phosphor-icons/react'
import { getDeviationAnalysisExportCopy, getDeviationAnalysisWidgetCopy, isKeyOf, LoadingSkeleton, Select, tokens } from 'ComponentLibrary'
import { IconButton, Typography } from 'ComponentLibrary/src/piazza'
import { InfoBox } from 'ComponentLibrary/src/piazza/info'
import { TextBubble, TextBubbleCreation, type TextBubbleCopy } from 'ComponentLibrary/src/widgets/Annotations'
import { DeviationAnnotation, GeneralAnnotation, isPdfAnnotation, PdfAnnotation } from '@einwert/valuation-api-client'
import { Fragment, useMemo, useState } from 'react'
import { useValuationPlausibility } from './ValuationPlausibility.data'
import { Checkbox } from 'ComponentLibrary/src/piazza/selectors'
import { TextWithEllipsis } from 'ComponentLibrary/src/piazza/text'
import { useTranslation } from 'react-i18next'
import type { UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type PlausibilityAnnotionsProps = {
    pdfAnnotations: PdfAnnotation[]
    deviationAnnotations: DeviationAnnotation[]
    generalAnnotations: GeneralAnnotation[]
    isImmediatelyVisible?: boolean
    hasMultipleDraftOptions?: boolean
    onlyShowCurrentDraftAnnotations: boolean
    setOnlyShowCurrentDraftAnnotations: (onlyShowCurrentDraftAnnotations: boolean) => void
    hasNewerDraft: boolean
    hasMultipleDrafts: boolean
    goToPage: (page: number) => void
    isValuationPlausibilityPending?: boolean
    textBubbleCopy: TextBubbleCopy
    userLinkCopy: Partial<UserLinkCopy>
}

export default function PlausibilityAnnotions({
    pdfAnnotations,
    deviationAnnotations,
    generalAnnotations,
    isImmediatelyVisible,
    hasMultipleDraftOptions,
    onlyShowCurrentDraftAnnotations,
    setOnlyShowCurrentDraftAnnotations,
    hasNewerDraft,
    hasMultipleDrafts,
    goToPage,
    isValuationPlausibilityPending,
    textBubbleCopy,
    userLinkCopy,
}: PlausibilityAnnotionsProps) {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tDeviationAnalysisWidget } = useTranslation('terminology', { keyPrefix: 'common.deviationAnalysisWidget' })
    const { t: tExport } = useTranslation('terminology', { keyPrefix: 'export' })
    const { t: tPlausibility } = useTranslation('terminology', { keyPrefix: 'plausibility' })
    const { t: tPlausibilityAnnotations } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valuation.valuationPlausibility.plausibilityAnnotations',
    })
    const locale = useCurrentResolvedLocale()
    const deviationAnalysisExportCopy = getDeviationAnalysisExportCopy(tCommon, tExport)
    const deviationAnalysisWidgetCopy = getDeviationAnalysisWidgetCopy(deviationAnalysisExportCopy, tDeviationAnalysisWidget)
    const getDeviationTableRowLabel = (key?: string): string => {
        if (key && isKeyOf(key, deviationAnalysisWidgetCopy.rowLabels)) {
            return deviationAnalysisWidgetCopy.rowLabels[key]
        }
        return key ?? ''
    }
    const {
        addAnnotation,
        addReply,
        editAnnotation,
        editReply,
        deleteAnnotation,
        deleteReply,
        currentUser,
        isAddAnnotationPending,
        isEditAnnotationPending,
        isDeleteAnnotationPending,
        valuationId,
    } = useValuationPlausibility()
    const [selectedAnnotationType, setSelectedAnnotationType] = useState('all')
    const [isCreatingAnnotation, setIsCreatingAnnotation] = useState(false)

    const hasAnnotations = pdfAnnotations.length > 0 || deviationAnnotations.length > 0 || generalAnnotations.length > 0

    const annotationsPerPage = useMemo(() => {
        return pdfAnnotations.reduce<Record<string, PdfAnnotation[]>>((acc, annotation) => {
            if (isPdfAnnotation(annotation)) {
                const page = `${annotation.page_number}`
                if (!acc[page]) {
                    acc[page] = []
                }
                acc[page].push(annotation)
            }
            return acc
        }, {})
    }, [pdfAnnotations])

    return (
        <Box sx={{ overflowY: 'scroll', scrollbarGutter: 'stable', width: '392px', boxSizing: 'content-box', flex: 1, minHeight: 0 }}>
            <Stack
                direction="column"
                gap={tokens.SizingSpacing100}
                padding={`${tokens.SizingSpacing100} ${tokens.SizingSpacing200}`}
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: tokens.ColorBackgroundLight,
                    borderBottom: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
                }}
            >
                <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                    <Select
                        formControlSx={{ width: '100%' }}
                        value={selectedAnnotationType}
                        options={[
                            { label: tPlausibilityAnnotations('filterOptions.all'), value: 'all' },
                            { label: tPlausibilityAnnotations('filterOptions.general'), value: 'general' },
                            { label: tCommon('deviationAnalysis'), value: 'deviation' },
                            { label: 'PDF', value: 'pdf' },
                        ]}
                        onChange={(e) => setSelectedAnnotationType(e.target.value)}
                        size="small"
                    />
                    <IconButton
                        variant="secondary"
                        size="small"
                        markedYellow
                        icon={<PlusCircleIcon />}
                        onClick={() => {
                            setIsCreatingAnnotation(true)
                        }}
                        tooltip={tPlausibilityAnnotations('newGeneralAnnotationTooltip')}
                        disabled={hasNewerDraft}
                    />
                </Stack>
                {hasMultipleDraftOptions && !isValuationPlausibilityPending && (
                    <Checkbox
                        label={tPlausibilityAnnotations('onlyThisDraft')}
                        checked={onlyShowCurrentDraftAnnotations}
                        onChange={() => setOnlyShowCurrentDraftAnnotations(!onlyShowCurrentDraftAnnotations)}
                    />
                )}
            </Stack>

            <Stack direction="column" gap={tokens.SizingSpacing150} padding={tokens.SizingSpacing200}>
                {isValuationPlausibilityPending ? (
                    <LoadingSkeleton variant="rounded" width="100%" height="114px" count={3} />
                ) : (
                    <>
                        {hasNewerDraft && (
                            <InfoBox variant="ordinary" size="small" icon={<InfoIcon />}>
                                <Typography variant="textS" color={tokens.ColorTextBold}>
                                    {tPlausibilityAnnotations('newerDraftInfo')}
                                </Typography>
                            </InfoBox>
                        )}
                        {!isImmediatelyVisible && !hasNewerDraft && (
                            <InfoBox variant="ordinary" size="small" icon={<InfoIcon />}>
                                <Typography variant="textXS" color={tokens.ColorTextInformativeSubtle}>
                                    {tPlausibilityAnnotations('notSharedInfo')}
                                </Typography>
                            </InfoBox>
                        )}
                        {!hasAnnotations && (
                            <Box textAlign="center">
                                <Typography variant="textS" color={tokens.ColorTextDisabled}>
                                    {tPlausibility('noAnnotations')}
                                </Typography>
                            </Box>
                        )}
                        {isCreatingAnnotation && (
                            <TextBubbleCreation
                                onCancel={() => setIsCreatingAnnotation(false)}
                                onSubmit={(text) => {
                                    if (selectedAnnotationType === 'pdf' || selectedAnnotationType === 'deviation') {
                                        setSelectedAnnotationType('all')
                                    }
                                    addAnnotation({ text, annotation_type: 'general' }, () => setIsCreatingAnnotation(false))
                                }}
                                currentUser={currentUser}
                                isPending={isAddAnnotationPending}
                                isImmediatelyVisible={isImmediatelyVisible}
                                copy={textBubbleCopy}
                                userLinkCopy={userLinkCopy}
                            />
                        )}

                        <Stack
                            direction="column"
                            gap={tokens.SizingSpacing150}
                            sx={{ '& > div:not(:first-of-type)': { paddingTop: tokens.SizingSpacing300 } }}
                        >
                            {(selectedAnnotationType === 'all' || selectedAnnotationType === 'general') &&
                                generalAnnotations.length > 0 && (
                                    <Stack direction="column" gap={tokens.SizingSpacing150}>
                                        <Typography variant="headline5" color={tokens.ColorTextSubtle}>
                                            {tPlausibilityAnnotations('sectionTitles.general')}
                                        </Typography>
                                        {generalAnnotations.map((annotation) => (
                                            <TextBubble
                                                key={annotation.uuid}
                                                type="inverted"
                                                annotation={annotation}
                                                addReply={addReply}
                                                editAnnotation={editAnnotation}
                                                editReply={editReply}
                                                deleteAnnotation={deleteAnnotation}
                                                deleteReply={deleteReply}
                                                currentUser={currentUser}
                                                isAddAnnotationPending={isAddAnnotationPending}
                                                isDeleteAnnotationPending={isDeleteAnnotationPending}
                                                isEditAnnotationPending={isEditAnnotationPending}
                                                isImmediatelyVisible={isImmediatelyVisible}
                                                showDraftVersion={hasMultipleDrafts}
                                                draftId={valuationId}
                                                hasNewerDraft={hasNewerDraft}
                                                copy={textBubbleCopy}
                                                userLinkCopy={userLinkCopy}
                                                locale={locale}
                                            />
                                        ))}
                                    </Stack>
                                )}
                            {(selectedAnnotationType === 'all' || selectedAnnotationType === 'deviation') &&
                                deviationAnnotations.length > 0 && (
                                    <Stack direction="column" gap={tokens.SizingSpacing150}>
                                        <Typography variant="headline5" color={tokens.ColorTextSubtle}>
                                            {tCommon('deviationAnalysis')}
                                        </Typography>
                                        {deviationAnnotations.map((annotation) => (
                                            <Fragment key={annotation.uuid}>
                                                <Stack direction="row">
                                                    <ReferenceBorder />
                                                    <Typography variant="textS" color={tokens.ColorTextDefault} fontStyle="italic">
                                                        &quot;{getDeviationTableRowLabel(annotation.row_reference)}&quot;
                                                    </Typography>
                                                </Stack>
                                                <TextBubble
                                                    key={annotation.uuid}
                                                    type="inverted"
                                                    annotation={annotation}
                                                    addReply={addReply}
                                                    editAnnotation={editAnnotation}
                                                    editReply={editReply}
                                                    deleteAnnotation={deleteAnnotation}
                                                    deleteReply={deleteReply}
                                                    currentUser={currentUser}
                                                    isAddAnnotationPending={isAddAnnotationPending}
                                                    isDeleteAnnotationPending={isDeleteAnnotationPending}
                                                    isEditAnnotationPending={isEditAnnotationPending}
                                                    isImmediatelyVisible={isImmediatelyVisible}
                                                    showDraftVersion={hasMultipleDrafts}
                                                    draftId={valuationId}
                                                    hasNewerDraft={hasNewerDraft}
                                                    copy={textBubbleCopy}
                                                    userLinkCopy={userLinkCopy}
                                                    locale={locale}
                                                />
                                            </Fragment>
                                        ))}
                                    </Stack>
                                )}
                            {(selectedAnnotationType === 'all' || selectedAnnotationType === 'pdf') && pdfAnnotations.length > 0 && (
                                <>
                                    {Object.entries(annotationsPerPage).map(([page, annotations]) => (
                                        <Stack direction="column" key={page} gap={tokens.SizingSpacing150}>
                                            <Stack
                                                direction="row"
                                                width="fit-content"
                                                gap={tokens.SizingSpacing075}
                                                onClick={() => {
                                                    goToPage(parseInt(page))
                                                }}
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover span': { textDecoration: 'underline', textUnderlinePosition: 'from-font' },
                                                }}
                                            >
                                                <Typography variant="headline5" color={tokens.ColorTextSubtle}>
                                                    PDF <span>{tPlausibilityAnnotations('pdfPageLabel', { page })}</span>
                                                </Typography>
                                                <ArrowUpRightIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconSubtle} />
                                            </Stack>
                                            {annotations.map((annotation) => (
                                                <Fragment key={annotation.uuid}>
                                                    {annotation.selection && (
                                                        <Stack direction="row">
                                                            <ReferenceBorder />
                                                            <TextWithEllipsis
                                                                typographyProps={{
                                                                    variant: 'textS',
                                                                    color: tokens.ColorTextDefault,
                                                                    fontStyle: 'italic',
                                                                    lineClamp: 4,
                                                                }}
                                                                tooltipText={`"${getDeviationTableRowLabel(annotation.selection?.text)}"`}
                                                            >
                                                                &quot;{getDeviationTableRowLabel(annotation.selection?.text)}&quot;
                                                            </TextWithEllipsis>
                                                        </Stack>
                                                    )}
                                                    <TextBubble
                                                        key={annotation.uuid}
                                                        type="inverted"
                                                        annotation={annotation}
                                                        addReply={addReply}
                                                        editAnnotation={editAnnotation}
                                                        editReply={editReply}
                                                        deleteAnnotation={deleteAnnotation}
                                                        deleteReply={deleteReply}
                                                        currentUser={currentUser}
                                                        isAddAnnotationPending={isAddAnnotationPending}
                                                        isDeleteAnnotationPending={isDeleteAnnotationPending}
                                                        isEditAnnotationPending={isEditAnnotationPending}
                                                        showDraftVersion={hasMultipleDrafts}
                                                        draftId={valuationId}
                                                        hasNewerDraft={hasNewerDraft}
                                                        copy={textBubbleCopy}
                                                        userLinkCopy={userLinkCopy}
                                                        locale={locale}
                                                    />
                                                </Fragment>
                                            ))}
                                        </Stack>
                                    ))}
                                </>
                            )}
                        </Stack>
                    </>
                )}
            </Stack>
        </Box>
    )
}

const ReferenceBorder = () => (
    <Stack direction="row" alignSelf="stretch">
        <Box
            sx={{
                borderLeft: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
                height: '100%',
                paddingLeft: tokens.SizingSpacing075,
            }}
        />
        <Box
            sx={{
                borderLeft: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
                height: '100%',
                paddingLeft: tokens.SizingSpacing075,
            }}
        />
    </Stack>
)
